import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import { Pagination } from '@twistopayments/design-system/components/atoms/pagination'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'

import type {
  BlogCategory,
  BlogPostCustom,
  PaginationCMS,
  SectionsBlogListComponent,
} from '@twisto/api-cms'
import { useSetupConfig } from '@twisto/base'
import { I18nProvider } from '@twisto/base/i18n-provider'
import { useBreakpoint } from '@twisto/hooks'

import { loadMessages } from 'homepage/load-messages'
import { getBlogCategories, getBlogPosts } from 'homepage/utils'

import {
  BlogListCategories,
  BlogListControls,
  BlogListNavigation,
  FeaturedPost,
  PostsGrid,
  PostsList,
} from '../../blog-components'

import * as styles from './blog-list.styles'

const FEATURED_ALLOWED_THRESHOLD = 2

type BlogListProps = {
  hasOffsets?: boolean
}

const defaultPaginationInfo: PaginationCMS = {
  page: 1,
  pageCount: 1,
  pageSize: 6,
  total: 1,
}

export const BlogList = ({
  title,
  highlighted,
  description,
  variant,
  cta,
  posts,
  hasOffsets = true,
  newest = false,
  blog_category: category,
  featured,
  showFeatured,
  showCategories,
}: SectionsBlogListComponent & BlogListProps) => {
  const isMobile = useBreakpoint('smDown')
  const containerRef = useRef<HTMLDivElement>(null)

  const config = useSetupConfig({})
  const { language } = config

  const [list, setList] = useState<BlogPostCustom[]>([])
  const [categories, setCategories] = useState<BlogCategory[] | null>(null)
  const [currentCategoryId, setCurrentCategoryId] = useState<
    string | number | undefined
  >(!showCategories ? category?.id : undefined)
  const [paginationInfo, setPaginationInfo] = useState<PaginationCMS>(
    defaultPaginationInfo
  )

  const featuredFromList =
    list.length >= FEATURED_ALLOWED_THRESHOLD ? list[0] : null
  const featuredPost = showFeatured ? featured || featuredFromList : null

  const visiblePosts = useMemo(() => {
    if (!featuredPost || isMobile || !showFeatured) {
      return list
    }

    return list.filter((post) => post.documentId !== featuredPost.documentId)
  }, [showFeatured, list, featuredPost, isMobile])

  const hasControls = variant === 'list' && !!visiblePosts.length
  const effectivePageSize = showFeatured
    ? defaultPaginationInfo.pageSize + 1
    : defaultPaginationInfo.pageSize

  useEffect(() => {
    if (!showCategories) {
      return
    }

    getBlogCategories().then((res) => setCategories(res))
  }, [showCategories])

  const loadPosts = useCallback(async () => {
    const res = await getBlogPosts(
      defaultPaginationInfo.page,
      effectivePageSize,
      currentCategoryId,
      newest
    )

    setPaginationInfo(res.meta.pagination)
    setList(res.posts)
  }, [newest, currentCategoryId, effectivePageSize])

  useEffect(() => {
    if (posts?.length) {
      setList(posts as BlogPostCustom[])

      return
    }

    loadPosts()
  }, [posts, newest, currentCategoryId, loadPosts])

  const renderPosts = () => {
    if (variant === 'list') {
      return <PostsList containerRef={containerRef} posts={visiblePosts} />
    }

    return <PostsGrid posts={visiblePosts} />
  }

  return (
    <section css={styles.section}>
      {hasControls && (
        <div css={[styles.head]}>
          <BlogListControls containerRef={containerRef} />
        </div>
      )}

      <div css={[styles.container, hasOffsets && styles.offsets]}>
        {showFeatured && featuredPost && (
          <FeaturedPost {...(featuredPost as BlogPostCustom)} />
        )}

        <BlogListNavigation
          containerRef={hasControls ? containerRef : undefined}
          hasControls={hasControls}
          highlighted={highlighted}
          isTitleCentered={showCategories && !hasControls}
          title={title}
        />

        {description && (
          <Typography css={styles.description} variant="b1">
            {description}
          </Typography>
        )}

        {showCategories && categories && (
          <BlogListCategories
            categories={categories}
            currentCategoryId={currentCategoryId}
            handleCategoryChange={setCurrentCategoryId}
          />
        )}

        {list.length > 0 && renderPosts()}
      </div>

      {cta?.text && cta.link && (
        <div css={styles.cta}>
          <TextButton href={cta.link} target="_blank">
            {cta.text}
          </TextButton>
        </div>
      )}

      {!posts?.length && (
        <I18nProvider language={language} loadMessages={loadMessages}>
          <div css={styles.pagination}>
            <Pagination
              page={paginationInfo.page}
              rowsCount={paginationInfo.total}
              rowsPerPage={paginationInfo.pageSize}
              onPageChange={async (page) => {
                await getBlogPosts(
                  page,
                  effectivePageSize,
                  currentCategoryId,
                  newest
                ).then((data) => {
                  setList(data.posts)
                  setPaginationInfo(data.meta.pagination)
                })
              }}
            />
          </div>
        </I18nProvider>
      )}
    </section>
  )
}
